<template>
    <div class="modal-overlay">
        <div class="modal-content">
            <h2>Progress History</h2>

            <!-- Read-only Goal Summary Section (same as GoalUpdateModal) -->
            <div class="goal-summary">
                <div class="goal-header">
                    <div class="goal-type-pill" :class="`type-${goal.goal_type_id}`">
                        {{ getGoalTypeName(goal.goal_type_id) }}
                    </div>
                    <h3>{{ goal.title }}</h3>
                </div>
                <p class="goal-description">{{ goal.description }}</p>
                <div class="goal-metrics">
                    <div class="metric">
                        <label>Target Value:</label>
                        <span>{{ goal.target_value }}</span>
                    </div>
                    <div class="metric">
                        <label>Due Date:</label>
                        <span>{{ formatDate(goal.target_date) }}</span>
                    </div>
                </div>
            </div>

            <!-- Progress Chart Section -->
            <div class="chart-container">
                <canvas ref="chartCanvas"></canvas>
            </div>

            <!-- Progress History List -->
            <div class="history-list">
                <div v-if="updates.length === 0" class="no-updates">
                    No progress updates yet
                </div>
                <div v-else v-for="update in updates" :key="update.id" class="history-item">
                    <div class="update-header">
                        <div class="update-date">
                            {{ formatDate(update.update_date) }}
                        </div>
                        <div class="progress-value">
                            Progress: {{ update.current_value }}
                            <span class="percentage">
                                ({{ calculatePercentage(update.current_value) }}%)
                            </span>
                        </div>
                    </div>
                    <div class="update-notes">
                        {{ update.notes }}
                    </div>
                </div>
            </div>

            <div class="modal-actions">
                <button class="close-btn" @click="handleClose">
                    Close
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { format, parseISO } from 'date-fns';
import { Chart } from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import { goalService } from '../services/goalService';
import { useRouter } from 'vue-router';

export default {
    name: 'GoalHistoryModal',
    props: {
        goal: {
            type: Object,
            required: true
        },
        goalTypes: {
            type: Object,
            required: true
        }
    },
    emits: ['close'],
    
    setup(props, { emit }) {
        const router = useRouter();
        const updates = ref([]);
        const chartCanvas = ref(null);
        let chart = null;

        const createChart = () => {
            if (!chartCanvas.value || !updates.value.length) return;
            
            const ctx = chartCanvas.value.getContext('2d');
            const startDate = parseISO(updates.value[updates.value.length - 1].update_date);
            const endDate = parseISO(props.goal.target_date);
            
            // Create data points including start (0) and target
            const dataPoints = [
                ...updates.value.map(update => ({
                    x: parseISO(update.update_date),
                    y: update.current_value
                }))
            ];
            
            chart = new Chart(ctx, {
                type: 'line',
                data: {
                    datasets: [
                        {
                            label: 'Progress',
                            data: dataPoints,
                            borderColor: '#4285f4',
                            tension: 0.4,
                            fill: false
                        },
                        {
                            label: 'Target',
                            data: [
                                { x: startDate, y: props.goal.target_value },
                                { x: endDate, y: props.goal.target_value }
                            ],
                            borderColor: '#ea4335',
                            borderDash: [5, 5],
                            tension: 0,
                            fill: false
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        x: {
                            type: 'time',
                            time: {
                                unit: 'day'
                            },
                            title: {
                                display: true,
                                text: 'Date'
                            }
                        },
                        y: {
                            beginAtZero: true,
                            title: {
                                display: true,
                                text: 'Progress'
                            },
                            suggestedMax: props.goal.target_value * 1.1
                        }
                    },
                    plugins: {
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    return `Progress: ${context.parsed.y}`;
                                }
                            }
                        }
                    }
                }
            });
        };

        const fetchUpdates = async () => {
            try {
                const history = await goalService.getGoalUpdates(props.goal.id);
                history.sort((a, b) => new Date(b.update_date) - new Date(a.update_date));
                updates.value = history;
                createChart();
            } catch (error) {
                console.error('Error fetching goal updates:', error);
            }
        };

        const formatDate = (date) => {
            return format(new Date(date), 'MMM dd, yyyy');
        };

        const getGoalTypeName = (typeId) => {
            return props.goalTypes[typeId] || 'Unknown Type';
        };

        const calculatePercentage = (value) => {
            return Math.round((value / props.goal.target_value) * 100);
        };

        const handleClose = () => {
            // Clear the URL parameter
            router.replace({ query: {} });
            // Then emit close event
            emit('close');
        };

        onMounted(() => {
            fetchUpdates();
        });

        onUnmounted(() => {
            if (chart) {
                chart.destroy();
            }
        });

        return {
            updates,
            formatDate,
            getGoalTypeName,
            calculatePercentage,
            chartCanvas,
            handleClose
        };
    }
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 24px;
    border-radius: 8px;
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
}

.goal-summary {
    background: #f8f9fa;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 24px;
}

.goal-header {
    display: flex;
    flex-direction: row-reverse;
    justify-content: center;
    gap: 12px;
    margin-bottom: 12px;
    position: relative;
}

.goal-type-pill {
    position: absolute;
    right: 0;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 0.9rem;
}

.goal-header h3 {
    margin: 0 auto;
    padding: 0 60px;
}

.goal-metrics {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-top: 16px;
}

.metric {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.metric label {
    font-size: 0.9rem;
    color: #666;
}

.close-btn {
    padding: 8px 16px;
    background: #4285f4;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
}

.close-btn:hover {
    background: #1976d2;
}

.history-list {
    margin-top: 24px;
    max-height: 400px;
    overflow-y: auto;
}

.history-item {
    padding: 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    margin-bottom: 12px;
    background: white;
}

.update-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
}

.update-date {
    font-weight: 500;
    color: #666;
}

.progress-value {
    color: #2196f3;
    font-weight: 500;
}

.percentage {
    color: #666;
    font-size: 0.9rem;
}

.update-notes {
    color: #444;
    font-size: 0.95rem;
    line-height: 1.4;
}

.no-updates {
    text-align: center;
    color: #666;
    padding: 24px;
    background: #f5f5f5;
    border-radius: 8px;
}

.chart-container {
    height: 200px;
    margin: 20px 0;
    padding: 16px;
    background: white;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
}

@media (max-width: 768px) {
    .goal-header {
        flex-direction: column;
        align-items: center;
        gap: 8px;
        margin-bottom: 16px;
    }

    .goal-type-pill {
        position: static;
        margin-bottom: 8px;
    }

    .goal-header h3 {
        padding: 0;
        text-align: center;
        font-size: 1.2rem;
        line-height: 1.4;
    }
}
</style> 